import { KitButton } from './button';
import { useState } from 'react';
import { usePhoneTable } from './table-window';
import { useBoardDrag } from './board-drag';
import type { BoardProps } from './board-types';
export type { BoardProps, BoardColumn, BoardItem } from './board-types';

export function Board(props: BoardProps) {
  const phone = usePhoneTable();
  const [selected, setSelected] = useState(props.columns[0]?.id);
  const active = props.columns.some((column) => column.id === selected)
    ? selected
    : props.columns[0]?.id;
  const model = useBoardDrag(props, phone);
  const status = props.copy.status(model.drag.snapshot);
  const moving =
    model.drag.gesture && props.items.find((item) => item.id === model.drag.gesture?.id);
  const targetRows = props.items.filter(
    (item) => item.columnId === model.target?.columnId && item.id !== moving?.id,
  );
  const position =
    model.target?.beforeId === null
      ? targetRows.length
      : targetRows.findIndex((item) => item.id === model.target?.beforeId);
  const announcement =
    moving && model.target
      ? props.copy.position(
          moving,
          position + 1,
          targetRows.length + 1,
          props.columns.find((column) => column.id === model.target?.columnId),
        )
      : model.drag.message;
  const columns = phone ? props.columns.filter((column) => column.id === active) : props.columns;
  return (
    <>
      {phone && (
        <div className="pc-board-switch" role="tablist">
          {props.columns.map((column) => (
            <KitButton
              key={column.id}
              type="button"
              role="tab"
              aria-selected={column.id === active}
              onClick={() => setSelected(column.id)}
            >
              {props.copy.tab(
                column,
                props.items.filter((item) => item.columnId === column.id).length,
              )}
            </KitButton>
          ))}
        </div>
      )}
      <div className="pc-board" ref={model.board}>
        {columns.map((column) => {
          const items = props.items.filter((item) => item.columnId === column.id);
          return (
            <section
              key={column.id}
              className="pc-board-col"
              data-column-id={column.id}
              data-state={
                model.drag.gesture && model.target?.columnId === column.id
                  ? 'drop-target'
                  : undefined
              }
            >
              <header className="pc-board-col-head">
                <h3>{column.label}</h3>
                <span className="pc-board-count">
                  {props.copy.count?.(items.length) ?? new Intl.NumberFormat().format(items.length)}
                </span>
              </header>
              <ol className="pc-board-cards">
                {items.map((item) => (
                  <li
                    key={item.id}
                    className="pc-board-card"
                    data-item-id={item.id}
                    data-state={model.drag.gesture?.id === item.id ? 'dragging' : undefined}
                    onClick={(event) => {
                      if (model.suppressClick.current) {
                        model.suppressClick.current = false;
                        return;
                      }
                      if (!model.drag.gesture && !(event.target as Element).closest('button'))
                        props.onOpen(item);
                    }}
                    onPointerDown={(event) => {
                      if (
                        phone ||
                        !model.allowed(item.id) ||
                        (event.target as Element).closest('button')
                      )
                        return;
                      model.pointer.current = {
                        id: item.id,
                        x: event.clientX,
                        y: event.clientY,
                        started: false,
                      };
                    }}
                    onPointerMove={(event) => {
                      const start = model.pointer.current;
                      if (!start || start.id !== item.id) return;
                      if (
                        !start.started &&
                        Math.hypot(event.clientX - start.x, event.clientY - start.y) >= 4
                      ) {
                        start.started = true;
                        model.suppressClick.current = true;
                        event.currentTarget.setPointerCapture(event.pointerId);
                        model.begin(item.id);
                      }
                      if (start.started) {
                        event.preventDefault();
                        model.point(event.clientX, event.clientY);
                      }
                    }}
                    onPointerUp={() => {
                      if (model.pointer.current?.started) model.drop();
                      model.pointer.current = null;
                    }}
                    onPointerCancel={model.cancel}
                  >
                    <h4>
                      <KitButton
                        variant="quiet"
                        type="button"
                        onClick={() => props.onOpen(item)}
                        onKeyDown={(event) => model.key(item.id, event)}
                      >
                        {item.label}
                      </KitButton>
                    </h4>
                    <div className="pc-board-meta">{item.meta?.slice(0, 3)}</div>
                    {item.value}
                    {model.allowed(item.id) &&
                      props.renderActions?.(item, (columnId) => {
                        void model.commit(item.id, columnId, null).catch(props.onError);
                      })}
                  </li>
                ))}
              </ol>
              {!items.length && <div className="pc-board-empty">{props.copy.empty(column)}</div>}
              {props.onAdd && (props.canAdd?.(column.id) ?? true) && (
                <KitButton
                  type="button"
                  className="pc-board-add"
                  disabled={model.drag.locked}
                  onClick={() => props.onAdd?.(column.id)}
                >
                  {props.copy.add(column)}
                </KitButton>
              )}
            </section>
          );
        })}
      </div>
      <div role="status" aria-live="polite">
        {status ?? announcement}
      </div>
    </>
  );
}
